import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { CartItem } from "./types";
import { OrderTypes } from "@/lib/supabase/enums";
import { getMenuPicksIdentity } from "@/lib/menu-picks";

interface CartStore {
  /**
   * Whose menu the cart was filled from. Merchants without a domain of their
   * own all share the stable QR host, and with it this localStorage entry.
   */
  merchantKey?: string;
  cart: {
    orderType: OrderTypes;
    items: Array<CartItem>;
    coupon?: { code: string; coupon?: any };
    fees: {
      deliveryFee: number;
      transactionFee: number;
    };
  };
  addToCart: (product: CartItem) => void;
  removeFromCart: (product: CartItem) => void;
  setOrderType: (orderType: OrderTypes) => void;
  setCoupon: (coupon?: { code: string; coupon?: any }) => void;
  updateCartItem: (cartItem: CartItem) => void;
  setDeliveryFee: (deliveryFee: number) => void;
  setTransactionFee: (transactionFee: number) => void;
  clearCart: () => void;
  bindToMerchant: (merchantKey: string) => void;
}

/** Splits a comma-joined multi-select value so selection order is ignored. */
function normalizeOptionValues(values?: Record<string, string>) {
  return Object.entries(values ?? {})
    .map(([key, value]) => [
      key,
      value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .sort()
        .join(","),
    ])
    .filter(([, value]) => value !== "")
    .sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Two cart lines are the same order line when nothing the kitchen or the bill
 * would see tells them apart: product, name (carries the variant), price,
 * options, menu picks (in any order, legacy or not) and comment.
 */
export function isSameCartLine(a: CartItem, b: CartItem) {
  return (
    a.productId === b.productId &&
    a.name === b.name &&
    a.price === b.price &&
    (a.comment?.trim() || "") === (b.comment?.trim() || "") &&
    JSON.stringify(normalizeOptionValues(a.selectedOptions)) ===
      JSON.stringify(normalizeOptionValues(b.selectedOptions)) &&
    getMenuPicksIdentity(a) === getMenuPicksIdentity(b)
  );
}

export const useCartStore = create<CartStore>()(
  persist(
    (set) => ({
      merchantKey: undefined,
      cart: {
        orderType: OrderTypes.delivery,
        items: [],
        fees: {
          deliveryFee: 0,
          transactionFee: 0,
        },
      },
      setCoupon: (coupon?: { code: string; coupon?: any }) =>
        set((state) => ({
          cart: { ...state.cart, coupon },
        })),
      setDeliveryFee: (deliveryFee: number) =>
        set((state) => ({
          cart: { ...state.cart, fees: { ...state.cart.fees, deliveryFee } },
        })),
      setTransactionFee: (transactionFee: number) =>
        set((state) => ({
          cart: { ...state.cart, fees: { ...state.cart.fees, transactionFee } },
        })),
      setOrderType: (orderType) =>
        set((state) => ({
          cart: { ...state.cart, orderType },
        })),
      addToCart: (product) =>
        set((state) => {
          // The same line again (the cart's "+" button), or a fresh add that is
          // indistinguishable from a line already in the cart.
          const sameLineIndex = state.cart.items.findIndex(
            (item) => item.id === product.id
          );
          const existingItemIndex =
            sameLineIndex !== -1
              ? sameLineIndex
              : state.cart.items.findIndex((item) =>
                  isSameCartLine(item, product)
                );

          if (existingItemIndex !== -1) {
            // Item exists, increment quantity
            const updatedItems = [...state.cart.items];
            updatedItems[existingItemIndex] = {
              ...updatedItems[existingItemIndex],
              quantity:
                updatedItems[existingItemIndex].quantity + product.quantity,
              enabled: product.enabled ?? true,
            };
            return {
              cart: {
                items: updatedItems,
                fees: state.cart.fees,
                orderType: state.cart.orderType,
                coupon: state.cart.coupon,
              },
            };
          } else {
            // Item doesn't exist, add new item
            return {
              cart: {
                items: [...state.cart.items, { ...product, enabled: product.enabled ?? true }],
                fees: state.cart.fees,
                orderType: state.cart.orderType,
                coupon: state.cart.coupon,
              },
            };
          }
        }),
      removeFromCart: (product) =>
        set((state) => {
          const existingItemIndex = state.cart.items.findIndex(
            (item) => item.id === product.id
          );

          if (existingItemIndex !== -1) {
            const existingItem = state.cart.items[existingItemIndex];
            const newQuantity = existingItem.quantity - product.quantity;

            if (newQuantity <= 0) {
              // Remove item if quantity becomes 0 or negative
              return {
                cart: {
                  items: state.cart.items.filter(
                    (item) => item.id !== product.id
                  ),
                  fees: state.cart.fees,
                  orderType: state.cart.orderType,
                  coupon: state.cart.coupon,
                },
              };
            } else {
              // Decrement quantity
              const updatedItems = [...state.cart.items];
              updatedItems[existingItemIndex] = {
                ...existingItem,
                quantity: newQuantity,
              };
              return {
                cart: {
                  items: updatedItems,
                  fees: state.cart.fees,
                  orderType: state.cart.orderType,
                  coupon: state.cart.coupon,
                },
              };
            }
          }

          // Item not found, return current state
          return state;
        }),
      updateCartItem: (cartItem) => {
        set((state) => ({
          cart: {
            items: state.cart.items.map((item) =>
              item.id === cartItem.id ? cartItem : item
            ),
            fees: state.cart.fees,
            orderType: state.cart.orderType,
            coupon: state.cart.coupon,
          },
        }));
      },
      clearCart: () =>
        set((state) => ({
          cart: {
            items: [],
            fees: state.cart.fees,
            orderType: state.cart.orderType,
            coupon: state.cart.coupon,
          },
        })),
      bindToMerchant: (merchantKey) =>
        set((state) => {
          if (state.merchantKey === merchantKey) return state;
          // A cart saved before carts were bound belongs to this host's only
          // merchant; keep it rather than empty it on the first visit.
          if (state.merchantKey === undefined) return { merchantKey };

          // Another merchant's products and coupon mean nothing here.
          return {
            merchantKey,
            cart: { ...state.cart, items: [], coupon: undefined },
          };
        }),
    }),
    {
      name: "cart-storage", // unique name for localStorage key
    }
  )
);
